/**
 * The one boundary between the screen and a game. Solo and Coach run the engine
 * in the browser; Online runs it on the server. The GameScreen never knows which.
 */

import type { GameEvent, Seat, SeatActionBody } from '@shared/engine';
import type { RoomInfo } from '@shared/protocol';
import type { View } from '@shared/view';
import type { Card } from '@shared/cards';

export type ConnStatus =
  | { k: 'local' }
  | { k: 'connecting' }
  | { k: 'open' }
  | { k: 'reconnecting'; attempt: number }
  | { k: 'closed'; reason: string };

export type Unsubscribe = () => void;

export interface GameAdapter {
  readonly mode: 'solo' | 'coach' | 'online';
  /** The six cards you were dealt this hand (for the coach's "seen" set). Local only knows this for sure; online infers from the first 6-card view. */
  dispatch(body: SeatActionBody): void;
  onView(cb: (view: View, events: GameEvent[]) => void): Unsubscribe;
  onStatus(cb: (s: ConnStatus) => void): Unsubscribe;
  onRoom(cb: (room: RoomInfo | null) => void): Unsubscribe;
  onError(cb: (code: string, message: string) => void): Unsubscribe;
  /** Latest known view, if any. */
  current(): View | null;
  /** Online: ask for a rematch / end an abandoned game / leave. No-ops locally. */
  rematch(): void;
  endAbandoned(): void;
  leave(): void;
  /** Solo/Coach: start a fresh game. */
  newGame(): void;
  close(): void;
}

export class Emitter<T extends unknown[]> {
  private subs = new Set<(...args: T) => void>();
  on(cb: (...args: T) => void): Unsubscribe {
    this.subs.add(cb);
    return () => this.subs.delete(cb);
  }
  emit(...args: T) {
    for (const s of [...this.subs]) s(...args);
  }
}

export type { View, GameEvent, Seat, SeatActionBody, RoomInfo, Card };
